import Search from "@mui/icons-material/Search";
import { useEffect, useMemo, useRef, useState } from "react";
import { industryInput1 } from "../../data/searchFlowText.js";
import {
  getIndustrySelectMeta,
  INDUSTRY_SELECT_ORDER,
} from "../../utils/industrySelectOptions";
import i18n from "../../../../utils/i18n";
import classes from "./companyIndustrySelect.module.scss";

const PINNED_WHEN_FILTERED = new Set(["otherservices", "other"]);

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const Radio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

const CompanyIndustrySelect = ({
  selectedIndustry = "",
  industryInput = "",
  setIndustrySelection,
  setIndustryInput,
}) => {
  const industryOptions = useMemo(() => industryInput1(), []);
  const [industrySearch, setIndustrySearch] = useState("");
  const [persistError, setPersistError] = useState("");
  const [isDesktopLayout, setIsDesktopLayout] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 900px)").matches,
  );
  const otherTextareaRef = useRef(null);
  const isOtherSelected = selectedIndustry === "other";

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 900px)");
    const handleChange = (event) => setIsDesktopLayout(event.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (!isOtherSelected || !otherTextareaRef.current) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => {
      otherTextareaRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      otherTextareaRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [isOtherSelected]);

  const normalizedSearch = normalizeText(industrySearch);

  const filteredIndustryOptions = useMemo(() => {
    const matchesSearch = (value) => {
      const meta = getIndustrySelectMeta(value);
      const haystack = normalizeText(`${meta.title} ${meta.subtitle}`);
      return haystack.includes(normalizedSearch);
    };

    const optionByValue = new Map(
      industryOptions.map((industry) => [industry.value, industry]),
    );

    let visibleValues = INDUSTRY_SELECT_ORDER.filter((value) =>
      optionByValue.has(value),
    );

    if (normalizedSearch) {
      const matching = new Set(
        visibleValues.filter((value) => matchesSearch(value)),
      );
      for (const value of PINNED_WHEN_FILTERED) {
        if (optionByValue.has(value)) {
          matching.add(value);
        }
      }
      visibleValues = visibleValues.filter((value) => matching.has(value));
    }

    const options = visibleValues
      .map((value) => optionByValue.get(value))
      .filter(Boolean);

    const withoutTail = options.filter(
      (option) => option.value !== "otherservices" && option.value !== "other",
    );
    const otherservicesOption = optionByValue.get("otherservices");
    const otherOption = optionByValue.get("other");

    if (otherservicesOption) {
      withoutTail.push(otherservicesOption);
    }
    if (otherOption) {
      withoutTail.push(otherOption);
    }

    return withoutTail;
  }, [industryOptions, normalizedSearch]);

  const handleSelect = async (industry) => {
    setPersistError("");
    try {
      await setIndustrySelection(industry);
    } catch (error) {
      console.error("Industry selection persist error:", error);
      setPersistError(i18n.t("searchflow.industryerror"));
    }
  };

  const renderIndustryCard = (industry) => {
    const { value } = industry;
    const { title, emoji, subtitle } = getIndustrySelectMeta(value);
    const isSelected = selectedIndustry === value;

    if (value === "other") {
      return (
        <div
          key={value}
          className={`${classes.cardExpandable} ${
            isSelected ? classes.cardExpandableSelected : ""
          }`}
        >
          <button
            type="button"
            className={classes.cardHeader}
            onClick={() => handleSelect(value)}
            aria-pressed={isSelected}
          >
            <div className={classes.cardTopRow}>
              <span className={classes.cardEmoji} role="img" aria-hidden="true">
                {emoji}
              </span>
              <Radio checked={isSelected} />
            </div>
            <div className={classes.cardContent}>
              <div className={classes.cardTitle}>{title}</div>
            </div>
          </button>

          {isSelected ? (
            <div className={classes.otherBody}>
              <label
                className={classes.otherLabel}
                htmlFor="company-industry-other-input"
              >
                {i18n.t("searchflow.industryotherlabel")}
              </label>
              <textarea
                id="company-industry-other-input"
                ref={otherTextareaRef}
                className={classes.otherTextarea}
                value={industryInput}
                onChange={(event) => setIndustryInput(event.target.value)}
                placeholder={i18n.t("searchflow.industryotherplaceholder")}
              />
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <button
        key={value}
        type="button"
        className={`${classes.card} ${isSelected ? classes.cardSelected : ""}`}
        onClick={() => handleSelect(value)}
        aria-pressed={isSelected}
      >
        <div className={classes.cardTopRow}>
          <span className={classes.cardEmoji} role="img" aria-hidden="true">
            {emoji}
          </span>
          <Radio checked={isSelected} />
        </div>
        <div className={classes.cardContent}>
          <div className={classes.cardTitle}>{title}</div>
          {subtitle ? <div className={classes.cardSubtitle}>{subtitle}</div> : null}
        </div>
      </button>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>
                {i18n.t("searchflow.industrytitle")}
              </h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.industrysubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.searchWrap}>
                <label
                  className={classes.searchLabel}
                  htmlFor="company-industry-search-input"
                >
                  <span className={classes.searchLabelMobile}>
                    {i18n.t("searchflow.industrysearchlabelmobile")}
                  </span>
                  <span className={classes.searchLabelDesktop}>
                    {i18n.t("searchflow.industrysearchlabeldesktop")}
                  </span>
                </label>
                <div className={classes.searchField}>
                  <Search className={classes.searchIcon} aria-hidden="true" />
                  <input
                    id="company-industry-search-input"
                    type="search"
                    className={classes.searchInput}
                    placeholder={
                      isDesktopLayout
                        ? i18n.t("searchflow.industrysearchplaceholderdesktop")
                        : i18n.t("searchflow.industrysearchplaceholdermobile")
                    }
                    value={industrySearch}
                    onChange={(event) => setIndustrySearch(event.target.value)}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className={classes.cardsGrid}>
                {filteredIndustryOptions.map(renderIndustryCard)}
                {normalizedSearch && filteredIndustryOptions.length === 0 ? (
                  <p className={classes.emptyState}>
                    {i18n.t("searchflow.industrynoresults")}
                  </p>
                ) : null}
              </div>

              {persistError ? (
                <p className={classes.persistError}>{persistError}</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyIndustrySelect;
